// Caixinhas das abas no My Planner (0.65, Texto 3 C). Substituem o card "Financeiro" do dia e o painel
// "Próximos vencimentos": cada aba que tem algo para o dia mostra uma caixinha própria, e você escolhe
// como ela aparece: oculta, resumida (só a linha com o total) ou completa (com os itens).
// Nada disso vira tarefa: as caixinhas só mostram. Contas aparecem 1, 3 ou 7 dias antes (Ajustes › Abas › Finanças).
// Na caixinha de Finanças dá para marcar a conta como paga (ou recebida) direto no My Planner.
import { useEffect, useMemo, useState } from 'react';
import { Alert, Pressable, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { setDoc, useAllDocs, useDoc } from '../store/store';
import type { AbaId, ContaFin } from '../store/types';
import { alternarFatura, alternarPago, faturasNoPeriodo, moedaDe, ocorrencias } from '../store/financas';
import { proximosVencimentos } from '../lib/vencimentos';
import { contagem } from '../modulos/util';
import { dinheiro, lerValor } from '../lib/moeda';
import { addDays, brYmd, parseYmd, today, ymd } from '../lib/date';
import { PickerModal } from './Pickers';
import { N_, t } from '../lib/i18n';
import { ActionSheet, Btn, Chip, Field, Sheet, type Action } from './ui';
import { C, aba, alpha, useCores } from './theme';
import { useCaixas } from '../lib/caixas';
import { LISTA_CONTAS, listaNoPlanner } from '../lib/enviarPlanner';

export type ModoCaixinha = 'oculta' | 'resumida' | 'completa';
export const MODOS_CAIXINHA: [ModoCaixinha, string][] = [['oculta', N_('Oculta')], ['resumida', N_('Resumida')], ['completa', N_('Completa')]];
export const DIAS_ANTES = [1, 3, 7];

type Item = { chave: string; aba: AbaId; id: string; icone: string; titulo: string; detalhe: string; data: string;
  valor?: number; tipo?: 'pagar' | 'receber'; feito?: boolean; marcar?: (valor?: number, data?: string, forma?: string) => void;
  /** 0.69 (B2 nas abas): a janela também pede a data e a forma de pagamento. */
  formas?: string[]; formaPadrao?: string };

export const modoDaCaixinha = (caixinhas: Partial<Record<AbaId, ModoCaixinha>> | undefined, id: AbaId): ModoCaixinha => caixinhas?.[id] ?? (id === 'diario' ? 'oculta' : 'resumida'); // Diário é privado: oculta por padrão
export const mudarModoCaixinha = (id: AbaId, m: ModoCaixinha) => setDoc('ajustes', d => ({ ...d, caixinhas: { ...(d.caixinhas ?? {}), [id]: m } }));

export function Caixinhas({ dia }: { dia: string }) {
  const docs = useAllDocs();
  const aj = useDoc('ajustes');
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const { liberada } = useCores();
  const hoje = today();
  const antes = aj.contasAntes ?? 3;
  const grupos = useMemo(() => {
    const itens: Item[] = [];
    // hoje: atrasados (até 7 dias) e o que vence nos próximos dias; outro dia: só o que vence nele
    const de = dia === hoje ? addDays(hoje, -7) : dia, ate = dia === hoje ? addDays(hoje, antes) : dia;
    const fin = docs.financas;
    if (liberada('financas') && fin && listaNoPlanner(aj, 'financas', LISTA_CONTAS)) {   // C2: só com as contas enviadas ao My Planner
      for (const c of fin.contas ?? []) for (const v of ocorrencias(c, de, ate)) {
        const pago = c.pagos.includes(v);
        if (pago && v !== dia) continue;
        itens.push({ chave: `c:${c.id}:${v}`, aba: 'financas', id: c.id, icone: c.icone || (c.tipo === 'pagar' ? '💸' : '💰'), titulo: c.nome,
          detalhe: c.tipo === 'pagar' ? t('Conta a pagar') : t('A receber'), data: v, valor: c.valor, tipo: c.tipo, feito: pago,
          marcar: (valor?: number) => alternarPago(c as ContaFin, v, valor) });
      }
      for (const f of faturasNoPeriodo(fin, de, ate)) {
        if (f.paga && f.venc !== dia) continue;
        itens.push({ chave: `f:${f.cartao.id}:${f.ym}`, aba: 'financas', id: f.cartao.id, icone: '💳', titulo: t('Fatura {c}', { c: f.cartao.nome }),
          detalhe: t('Conta a pagar'), data: f.venc, valor: f.total, tipo: 'pagar', feito: f.paga, marcar: () => alternarFatura(f.cartao.id, f.ym) });
      }
    }
    for (const v of proximosVencimentos(docs, liberada, 30, { de, ate, semFinancas: true }))
      itens.push({ chave: `${v.aba}:${v.id}:${v.data}:${v.detalhe}`, aba: v.aba, id: v.id, icone: v.icone, titulo: v.titulo, detalhe: v.detalhe, data: v.data });
    const porAba = new Map<AbaId, Item[]>();
    for (const i of itens.sort((a, b) => a.data.localeCompare(b.data))) porAba.set(i.aba, [...(porAba.get(i.aba) ?? []), i]);
    return [...porAba.entries()];
  }, [docs, aj, liberada, dia, hoje, antes]);
  const visiveis = grupos.filter(([id]) => modoDaCaixinha(aj.caixinhas, id) !== 'oculta');
  if (!visiveis.length) return null;
  return <View style={{ gap: cx.gap(8) }}>{visiveis.map(([id, l]) => <Caixinha key={id} id={id} itens={l} modo={modoDaCaixinha(aj.caixinhas, id)} dia={dia} />)}</View>;
}

function Caixinha({ id, itens, modo, dia }: { id: AbaId; itens: Item[]; modo: ModoCaixinha; dia: string }) {
  const router = useRouter();
  const { corDe } = useCores();
  const fin = useDoc('financas');
  const cx = useCaixas();
  const cor = corDe(id);
  const a = aba(id);
  const [aberta, setAberta] = useState(modo === 'completa');
  // trocar o modo (Resumida ↔ Completa) pelo ⋮ abre ou fecha a caixinha na hora (antes ficava como estava)
  useEffect(() => { setAberta(modo === 'completa'); }, [modo]);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const [pagar, setPagar] = useState<Item | null>(null);
  const moeda = moedaDe(fin);
  const $ = (v: number) => dinheiro(v, moeda);
  const abertos = itens.filter(i => !i.feito);
  const aPagar = abertos.filter(i => i.tipo === 'pagar').reduce((s, i) => s + (i.valor ?? 0), 0);
  const aReceber = abertos.filter(i => i.tipo === 'receber').reduce((s, i) => s + (i.valor ?? 0), 0);
  const atrasados = abertos.filter(i => i.data < today()).length;
  const ir = (item: string) => router.push({ pathname: '/aba/[id]', params: { id, item } } as any);
  const opcoes = () => setMenu({ t: `${a.icon} ${a.nome}`, a: [
    ...MODOS_CAIXINHA.map(([m, r]) => ({ label: (m === modo ? '✓ ' : '') + t('Caixinha {m}', { m: t(r).toLowerCase() }), onPress: () => mudarModoCaixinha(id, m) })),
    { label: '↗️ ' + t('Abrir {aba}', { aba: a.nome }), onPress: () => router.push({ pathname: '/aba/[id]', params: { id } } as any) },
  ] });
  const marcar = (i: Item) => {
    if (i.feito) return Alert.alert(i.tipo === 'pagar' ? t('Desmarcar como paga?') : t('Desmarcar como recebida?'), i.titulo, [
      { text: t('Cancelar'), style: 'cancel' }, { text: t('Desmarcar'), onPress: () => i.marcar?.() }]);
    if (i.chave.startsWith('f:')) return i.marcar?.();
    setPagar(i);
  };
  return (
    <View style={{ backgroundColor: C.card, borderRadius: cx.raio(16), ...cx.pads(12), gap: cx.gap(8), borderWidth: 1, borderColor: alpha(cor, 0.4) }}>
      <Pressable onPress={() => setAberta(x => !x)} onLongPress={opcoes} delayLongPress={1000} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ color: C.mute }}>{aberta ? '▾' : '▸'}</Text>
        <Text style={{ fontSize: 16 }}>{a.icon}</Text>
        <Text style={{ color: C.text, fontWeight: '800', flex: 1 }} numberOfLines={1}>
          {a.curto ?? a.nome} <Text style={{ color: C.sub, fontWeight: '600' }}>({abertos.length})</Text>
          {atrasados ? <Text style={{ color: C.danger, fontSize: 12 }}> · {t('{n} atrasado(s)', { n: atrasados })}</Text> : null}
        </Text>
        {aPagar > 0 && <Text style={{ color: C.danger, fontWeight: '700', fontSize: 12 }}>− {$(aPagar)}</Text>}
        {aReceber > 0 && <Text style={{ color: C.ok, fontWeight: '700', fontSize: 12 }}>+ {$(aReceber)}</Text>}
        <Pressable onPress={opcoes} hitSlop={10} accessibilityLabel={t('Mais opções')}><Text style={{ color: C.sub, fontSize: 18, fontWeight: '800' }}>⋮</Text></Pressable>
      </Pressable>
      {aberta && itens.map(i => {
        const c = i.data !== dia ? contagem(i.data) : null;
        return (
          <Pressable key={i.chave} onPress={() => ir(i.id)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, opacity: i.feito ? 0.6 : 1 }}>
            {i.marcar ? (
              <Pressable onPress={() => marcar(i)} hitSlop={8} accessibilityLabel={i.tipo === 'pagar' ? t('Marcar como paga') : t('Marcar como recebida')}
                style={{ width: 24, height: 24, borderRadius: 7, borderWidth: 2, borderColor: i.feito ? C.ok : cor, backgroundColor: i.feito ? C.ok : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
                {i.feito && <Text style={{ color: '#fff', fontWeight: '900', fontSize: 12 }}>✓</Text>}
              </Pressable>
            ) : <Text style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{i.icone}</Text>}
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '600', textDecorationLine: i.feito ? 'line-through' : 'none' }} numberOfLines={1}>{i.titulo}</Text>
              <Text style={{ color: C.sub, fontSize: 11 }} numberOfLines={1}>{i.detalhe}{i.data !== dia ? ' · ' + brYmd(i.data) : ''}</Text>
            </View>
            {i.valor != null && <Text style={{ color: i.tipo === 'pagar' ? C.danger : C.ok, fontWeight: '800', fontSize: 13 }}>{i.tipo === 'pagar' ? '−' : '+'} {$(i.valor)}</Text>}
            {!!c && !i.feito && <Text style={{ color: c.cor, fontWeight: '700', fontSize: 11 }}>{c.txt}</Text>}
          </Pressable>
        );
      })}
      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
      {!!pagar && <ValorPago item={pagar} cor={cor} moeda={moeda} onFechar={() => setPagar(null)} />}
    </View>
  );
}

/** Ao marcar como paga: confirma o valor e mostra a diferença (desconto, ou multa e juros). */
export function ValorPago({ item, cor, moeda, onFechar }: { item: Item; cor: string; moeda: string; onFechar: () => void }) {
  const [txt, setTxt] = useState(String(item.valor ?? '').replace('.', ','));
  const v = lerValor(txt), dif = Math.round((v - (item.valor ?? 0)) * 100) / 100;
  const pagar = item.tipo === 'pagar';
  const [data, setData] = useState(today());
  const [forma, setForma] = useState(item.formaPadrao ?? item.formas?.[0] ?? '');
  const [cal, setCal] = useState(false);
  return (
    <Sheet visible onClose={onFechar} title={pagar ? t('Marcar como paga') : t('Marcar como recebida')}>
      <View style={{ padding: 16, gap: 12 }}>
        <Text style={{ color: C.text, fontWeight: '700', fontSize: 16 }}>{item.icone} {item.titulo}</Text>
        <Text style={{ color: C.sub }}>{t('Valor previsto: {v}', { v: dinheiro(item.valor ?? 0, moeda) })} · {brYmd(item.data)}</Text>
        <Text style={{ color: C.sub, fontSize: 13 }}>{pagar ? t('Quanto você pagou?') : t('Quanto você recebeu?')}</Text>
        <Field value={txt} onChangeText={setTxt} keyboardType="decimal-pad" autoFocus />
        {!!item.formas && (<>
          <Text style={{ color: C.sub, fontSize: 13 }}>{pagar ? t('Data do pagamento') : t('Data do recebimento')}</Text>
          <View style={{ flexDirection: 'row' }}><Chip cor={cor} on label={'📅 ' + brYmd(data)} onPress={() => setCal(true)} /></View>
          {!!item.formas.length && (<>
            <Text style={{ color: C.sub, fontSize: 13 }}>{t('Forma de pagamento')}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {item.formas.map(f => <Chip key={f} small cor={cor} on={forma === f} label={t(f)} onPress={() => setForma(f)} />)}
            </View>
          </>)}
        </>)}
        {cal && <PickerModal mode="date" cor={cor} value={parseYmd(data)} onClose={() => setCal(false)} onPick={d => { setCal(false); setData(ymd(d)); }} />}
        {v > 0 && dif !== 0 && (
          <Text style={{ color: (pagar ? dif < 0 : dif > 0) ? C.ok : C.danger, fontWeight: '700' }}>
            {pagar ? (dif < 0 ? '🏷️ ' + t('Desconto de {v}', { v: dinheiro(-dif, moeda) }) : '⚠️ ' + t('Multa e juros de {v}', { v: dinheiro(dif, moeda) }))
              : (dif > 0 ? t('Recebeu {v} a mais', { v: dinheiro(dif, moeda) }) : t('Recebeu {v} a menos', { v: dinheiro(-dif, moeda) }))}
          </Text>
        )}
        <Btn cor={cor} label={'✓ ' + (pagar ? t('Confirmar pagamento') : t('Confirmar recebimento'))} onPress={() => {
          if (!(v > 0)) return Alert.alert(t('Informe o valor.'));
          if (item.formas) item.marcar?.(v, data, forma || undefined); else item.marcar?.(v);
          onFechar();
        }} />
      </View>
    </Sheet>
  );
}
